export interface OAuthAuthorizeURLInput {
  authorizeURL: string;
  clientID: string;
  redirectURI: string;
  scope: string;
  state: string;
  extraAuthorizeParams?: Record<string, string>;
  codeChallenge?: string;
}

export interface OAuthCallbackParams {
  code: string;
  state: string;
  error: string;
  errorDescription: string;
}

export interface OAuthTransaction {
  flowID: string;
  provider: string;
  appID: string;
  nonce: string;
  state: string;
  redirectURI: string;
  returnPath: string;
  codeVerifier?: string;
  createdAt: number;
}

const OAUTH_TRANSACTION_STORAGE_KEY = 'account.oauth.transaction';
export const OAUTH_CALLBACK_STORAGE_KEY_PREFIX = 'account.oauth.result.';

export interface OAuthStatePayload {
  flowID: string;
  provider: string;
  returnPath: string;
  nonce: string;
}

export interface OAuthCallbackStoragePayload {
  flowID: string;
  callbackURL: string;
  receivedAt: number;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let output = '';
  for (const byte of bytes) {
    output += String.fromCharCode(byte);
  }
  return btoa(output)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

function base64UrlDecode(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding =
    normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  const decoded = atob(normalized + padding);
  const bytes = new Uint8Array(decoded.length);
  for (let index = 0; index < decoded.length; index += 1) {
    bytes[index] = decoded.charCodeAt(index);
  }
  return bytes;
}

export function buildOAuthAuthorizeURL(input: OAuthAuthorizeURLInput): string {
  const url = new URL(input.authorizeURL);
  url.searchParams.set('client_id', input.clientID);
  url.searchParams.set('redirect_uri', input.redirectURI);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', input.scope);
  url.searchParams.set('state', input.state);

  for (const [key, value] of Object.entries(input.extraAuthorizeParams ?? {})) {
    if (value) {
      url.searchParams.set(key, value);
    }
  }

  if (input.codeChallenge) {
    url.searchParams.set('code_challenge', input.codeChallenge);
    url.searchParams.set('code_challenge_method', 'S256');
  }

  return url.toString();
}

export function readOAuthCallbackParams(
  callbackURL: string,
): OAuthCallbackParams {
  const url = new URL(callbackURL);
  return {
    code: url.searchParams.get('code') || '',
    state: url.searchParams.get('state') || '',
    error: url.searchParams.get('error') || '',
    errorDescription: url.searchParams.get('error_description') || '',
  };
}

export function createRandomState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export function encodeOAuthState(input: OAuthStatePayload): string {
  return base64UrlEncode(
    new TextEncoder().encode(
      JSON.stringify({
        flow_id: input.flowID,
        provider: input.provider,
        return_path: input.returnPath,
        nonce: input.nonce,
      }),
    ),
  );
}

export function decodeOAuthState(state: string): OAuthStatePayload {
  const decoded = new TextDecoder().decode(base64UrlDecode(state));
  const parsed = JSON.parse(decoded) as {
    flow_id?: unknown;
    provider?: unknown;
    return_path?: unknown;
    nonce?: unknown;
  };

  if (
    typeof parsed.flow_id !== 'string' ||
    typeof parsed.provider !== 'string' ||
    typeof parsed.return_path !== 'string' ||
    typeof parsed.nonce !== 'string'
  ) {
    throw new Error('登录状态校验失败，请重新登录');
  }

  return {
    flowID: parsed.flow_id,
    provider: parsed.provider,
    returnPath: parsed.return_path,
    nonce: parsed.nonce,
  };
}

export function createCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function createCodeChallenge(
  codeVerifier: string,
): Promise<string> {
  const bytes = new TextEncoder().encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return base64UrlEncode(new Uint8Array(digest));
}

export function saveOAuthTransaction(transaction: OAuthTransaction): void {
  window.sessionStorage.setItem(
    OAUTH_TRANSACTION_STORAGE_KEY,
    JSON.stringify(transaction),
  );
}

export function consumeOAuthTransaction(): OAuthTransaction | null {
  const raw = window.sessionStorage.getItem(OAUTH_TRANSACTION_STORAGE_KEY);
  window.sessionStorage.removeItem(OAUTH_TRANSACTION_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as OAuthTransaction;
  } catch {
    return null;
  }
}

export function getOAuthCallbackStorageKey(flowID: string): string {
  return `${OAUTH_CALLBACK_STORAGE_KEY_PREFIX}${flowID}`;
}

export function createOAuthCallbackStoragePayload(
  payload: OAuthCallbackStoragePayload,
): OAuthCallbackStoragePayload {
  return {
    flowID: payload.flowID,
    callbackURL: payload.callbackURL,
    receivedAt: payload.receivedAt,
  };
}

export function isOAuthCallbackStoragePayload(
  value: unknown,
): value is OAuthCallbackStoragePayload {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<OAuthCallbackStoragePayload>;
  return (
    typeof candidate.flowID === 'string' &&
    typeof candidate.callbackURL === 'string' &&
    typeof candidate.receivedAt === 'number'
  );
}
